// Utility functions for properties management
// Uses JSON file as primary source, localStorage as backup

const STORAGE_KEY = 'fa_real_estate_properties';
const ADMIN_KEY = 'fa_real_estate_admin';
const PROPERTIES_FILE = '/data/properties.json';

// Load properties from JSON file
export const loadPropertiesFromFile = async () => {
    try {
        const response = await fetch(PROPERTIES_FILE);
        if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data) && data.length > 0) {
                // Also save to localStorage as backup
                savePropertiesToLocalStorage(data);
                return data;
            }
        }
    } catch (error) {
        console.error('Error loading properties from file:', error);
    }
    return null;
};

// Get properties from localStorage (backup)
export const getPropertiesFromLocalStorage = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed) && parsed.length > 0) {
                return parsed;
            }
        }
    } catch (error) {
        console.error('Error reading from localStorage:', error);
    }
    return null;
};

// Save properties to localStorage (backup)
export const savePropertiesToLocalStorage = (properties) => {
    try {
        if (Array.isArray(properties)) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(properties));
        }
    } catch (error) {
        console.error('Error saving to localStorage:', error);
    }
};

// Get properties - tries file first, then localStorage, then defaults
export const getProperties = async (defaultProperties) => {
    // Try loading from file first
    const fileData = await loadPropertiesFromFile();
    if (fileData) {
        return fileData;
    }
    
    // Fallback to localStorage
    const localData = getPropertiesFromLocalStorage();
    if (localData) {
        return localData;
    }
    
    // Return defaults
    return defaultProperties || [];
};

// Save properties - saves to both localStorage and triggers file download
export const saveProperties = (properties) => {
    try {
        if (Array.isArray(properties)) {
            // Save to localStorage
            savePropertiesToLocalStorage(properties);
            
            // Note: Direct file write is not possible from frontend
            // We'll provide export functionality instead
            return true;
        }
    } catch (error) {
        console.error('Error saving properties:', error);
    }
    return false;
};

// Export properties to JSON file (download)
export const exportPropertiesToFile = (properties) => {
    try {
        const dataStr = JSON.stringify(properties, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'properties.json';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return true;
    } catch (error) {
        console.error('Error exporting properties:', error);
        return false;
    }
};

// Import properties from JSON file
export const importPropertiesFromFile = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = JSON.parse(e.target.result);
                if (Array.isArray(data)) {
                    saveProperties(data);
                    resolve(data);
                } else {
                    reject(new Error('Invalid file format'));
                }
            } catch (error) {
                reject(error);
            }
        };
        reader.onerror = reject;
        reader.readAsText(file);
    });
};

// Initialize with default properties if storage is empty
export const initializeProperties = async (defaultProperties) => {
    const properties = await getProperties(defaultProperties);
    if (properties.length === 0 && defaultProperties) {
        saveProperties(defaultProperties);
        return defaultProperties;
    }
    return properties;
};

// Check if admin is logged in
export const isAdminLoggedIn = () => {
    return localStorage.getItem(ADMIN_KEY) === 'true';
};

// Set admin login status
export const setAdminLogin = (status) => {
    if (status) {
        localStorage.setItem(ADMIN_KEY, 'true');
    } else {
        localStorage.removeItem(ADMIN_KEY);
    }
};

// Admin credentials (in production, this should be on a backend)
export const ADMIN_CREDENTIALS = {
    username: 'admin',
    password: 'admin123' // Change this in production!
};
